import { CenterAvailableDTO, CenterDTO } from '@/models/CenterDTO';
import { DateAvailabilityResponse } from '@/models/DateAvailabilityResponse';
import { EventDTO } from '@/models/EventDTO';
import { SelectOptionDTO } from '@/models/SelectOptionDTO';
import AppointmentService from '@/services/appointment.service';
import { ActionTypes, GetterTypes } from '@/store-vuex/types';
import moment from 'moment-timezone';
import { Component, Vue, Watch } from 'vue-property-decorator';
import InputSelect from '../inputs/InputSelect/InputSelect';
import CenterService from '@/services/center.service';
import { AppointmentDTO } from '@/models/AppointmentDTO';
import InformationBlock from '../InformationBlock/InformationBlock';
import { McsisConstants } from '@/models/constants/McsisConstants';
import WeekStrip from '../WeekStrip/WeekStrip';
import TimeSlotGrid from '../TimeSlotGrid/TimeSlotGrid';


@Component({
    components: {
        InputSelect,
        InformationBlock,
        WeekStrip,
        TimeSlotGrid,
    },
})
export default class AvailableAppointments extends Vue {
    public EventsAvailable: EventDTO[] = [];
    public referenceDate: string = '';
    public options: SelectOptionDTO[] = [];
    public maxDate: string = '';
    public minDate: string = '';
    public availabilitySummary: DateAvailabilityResponse[] = [];
    public selectedSlot: EventDTO | null = null;
    public slotsLoading: boolean = false;

    /**
     * Gets the data loading state of the application
     * @returns {boolean} True if the data is loading, otherwise false
     */
    public get loading(): boolean {
        return this.$store.getters[GetterTypes.dataLoading];
    }

    /**
     * Function called in the .vue file in order to format the selected center start date
     *
     * @param date The startDate field value
     * @returns The formatted date without time
     */
    public formatCenterDate(date: string): string {
        return moment(date).format(McsisConstants.DefaultDateFormat);
    }

    /**
     * Handle date selected from WeekStrip
     */
    public onDateSelected(dateStr: string): void {
        this.referenceDate = dateStr;
    }

    /**
     * Function called when the selected center changes
     *
     * @param selectedValue The new selected center
     */
    public onSelectChange(selectedValue: string) {
        // change value of center selected in the store
        this.$store.dispatch(ActionTypes.setCenterSelected, this.centerAvailable.find(
            (center: CenterAvailableDTO) => center.personId === Number(selectedValue)));

        // Update min date based on center
        this.minDate = moment(this.centerSelected.startDate).format(McsisConstants.DefaultDateFormat);
        this.referenceDate = this.minDate;

        // Fetch availability summary for new center
        this.fetchAvailabilitySummary();
        this.generateDates();
    }

    /**
     * Function called when an available slot time is selected
     *
     * @param event The EventDTO instance for the selected slot time
     */
    public async onEventClick(event: EventDTO) {
        const app = this.$store.getters[GetterTypes.appointment];
        const center = await CenterService.getCenterById(Number(this.centerSelected.personId));

        app.appDate = moment(event.start).format();
        app.appEndDate = moment(event.end).format();
        app.slotId = event.slotId;
        app.rooId = event.rooId;
        app.center = center as CenterDTO;

        // Update selected slot for visual feedback
        this.selectedSlot = event;

        if (app.appId !== undefined) {
            this.$store.commit(ActionTypes.setIsRdvModified, true);
        }
        this.$store.commit(ActionTypes.setAppointment, app);
        this.$emit('eventSelected');

        this.generateDates();
    }

    /**
     * Gets the centers with availabilities
     *
     * @returns {CenterAvailableDTO[]} The centers with availabilities
     */
    public get centerAvailable() {
        return this.$store.getters[GetterTypes.centerWithAvailabilities];
    }

    /**
     * Gets the selected center
     *
     * @returns {CenterAvailableDTO} The selected center
     */
    public get centerSelected(): CenterAvailableDTO {
        return this.$store.getters[GetterTypes.centerSelected];
    }

    /**
     * Updates the available slots when the selected date changes
     */
    @Watch('referenceDate')
    protected onReferenceDateChanged() {
        if (this.centerSelected.personId !== undefined) {
            this.generateDates();
        }
    }

    /**
     * Initializes the component data
     */
    protected async mounted() {
        this.$store.dispatch(ActionTypes.setDataLoading, true);
        await this.fetchCenterAvailable();
        this.generateOptions();

        await AppointmentService.GetMaxGeneratedSlotDate()
            .then((res: Date) => {
                this.maxDate = moment(res).format(McsisConstants.DefaultDateFormat);
            });

        if (this.appointment.center != null) {
            const ecId = this.appointment.center.personId;
            if (this.centerAvailable.find((center: CenterAvailableDTO) =>
                    center.personId === Number(ecId)) !== undefined) {
                this.$store.dispatch(ActionTypes.setCenterSelected, this.centerAvailable.find(
                    (center: CenterAvailableDTO) => center.personId === Number(ecId)),
                );
            }
        }

        // Set min date from center
        if (this.centerSelected.personId !== undefined) {
            this.minDate = moment(this.centerSelected.startDate).format(McsisConstants.DefaultDateFormat);
        } else {
            this.minDate = moment().format(McsisConstants.DefaultDateFormat);
        }

        // Set initial reference date
        if (this.appointment.appDate != null && moment(this.appointment.appDate) > moment()) {
            this.referenceDate = moment(this.appointment.appDate).format(McsisConstants.DefaultDateFormat);
            // Set selected slot if editing
            if (this.appointment.slotId) {
                this.selectedSlot = {
                    slotId: this.appointment.slotId,
                    start: this.appointment.appDate,
                    end: this.appointment.appEndDate || this.appointment.appDate,
                    rooId: this.appointment.rooId,
                    centerId: this.appointment.center ? this.appointment.center.personId : 0,
                    color: 'green',
                    name: ''
                };
            }
        } else if (this.centerSelected.personId !== undefined) {
            this.referenceDate = moment(this.centerSelected.startDate).format(McsisConstants.DefaultDateFormat);
        } else {
            this.referenceDate = moment().format(McsisConstants.DefaultDateFormat);
        }

        // Fetch availability summary
        await this.fetchAvailabilitySummary();

        this.$store.dispatch(ActionTypes.setDataLoading, false);
    }

    /**
     * @brief Get center list
     */
    protected async fetchCenterAvailable() {
        this.$store.dispatch(ActionTypes.setCenterWithAvailabilities, []);
        if (this.appointment.partId) {
            await AppointmentService.GetFirstSlot(this.rdvLong, this.appointment.partId)
                .then((res: any) => {
                    this.$store.dispatch(ActionTypes.setCenterWithAvailabilities, res as CenterAvailableDTO[]);
                });
        }
    }

    /**
     * Fetch availability summary for the selected center
     */
    protected async fetchAvailabilitySummary() {
        if (!this.centerSelected.personId || !this.appointment.partId || !this.minDate || !this.maxDate) {
            this.availabilitySummary = [];
            return;
        }

        this.availabilitySummary = await AppointmentService.GetAvailabilitySummary(
            this.centerSelected.personId,
            this.appointment.partId,
            this.rdvLong,
            this.minDate,
            this.maxDate
        );
    }

    /**
     * @brief fetch free slots
     */
    protected async generateDates() {
        if (this.centerSelected != null && this.referenceDate < this.formatCenterDate(this.centerSelected.startDate)) {
            this.EventsAvailable = [];
        } else if (this.appointment.partId) {
            this.slotsLoading = true;
            await AppointmentService.GetSlotsByCenterId(
                this.centerSelected.personId,
                this.appointment.partId,
                this.referenceDate,
                this.rdvLong)
                .then((res: EventDTO[]) => {
                    this.EventsAvailable = res;
                })
                .catch(() => {
                    this.EventsAvailable = [];
                })
                .finally(() => {
                    this.slotsLoading = false;
                });
        }
    }

    /**
     * Gets true if the appointment is a long one, otherwise false
     */
    private get rdvLong(): boolean {
        return this.$store.getters[GetterTypes.isRdvLong];
    }

    /**
     * Gets the current AppointmentDTO instance
     *
     * @returns {AppointmentDTO} The current AppointmentDTO instance
     */
    private get appointment(): AppointmentDTO {
        return this.$store.getters[GetterTypes.appointment];
    }

    /**
     * Generates the list of values for the center dropdown list with availabilities
     */
    private generateOptions() {
        let text = '';
        this.centerAvailable.forEach((center: CenterAvailableDTO) => {
            text = center.companyName + ' ('
                + this.$t('page.appointmentSelection.nextAvailability')
                + moment(center.startDate).format(McsisConstants.SwissDateFormat) + ')';

            this.options.push({ value: center.personId.toString(), text } as SelectOptionDTO);
        });
    }
}
